// Clips 1, 2 and 6 stay on LFS but read as too busy behind the loader label.
const CARTOON_LOADER_CLIP_NUMBERS = [3, 4, 5] as const

export const CARTOON_LOADER_CLIP_COUNT = CARTOON_LOADER_CLIP_NUMBERS.length

export type CartoonLoaderFormat = 'webm' | 'hevc'
export type CartoonLoaderSize = '1x' | '3x'

export const CARTOON_LOADER_SMALL_SCREEN_QUERY = '(max-width: 767px)'

export function cartoonLoaderClipUrls(
  lfsUrl: string,
  format: CartoonLoaderFormat = 'webm',
  size: CartoonLoaderSize = '3x',
) {
  return CARTOON_LOADER_CLIP_NUMBERS.map(clipNumber => {
    const sizeSuffix = size === '1x' ? '-1x' : ''
    const base = `${lfsUrl}/web/tools/cartoon-loader/cartoon-loader-${clipNumber}${sizeSuffix}`
    return format === 'hevc' ? `${base}-hevc.mp4` : `${base}.webm`
  })
}

// Safari decodes VP9 WebM but drops its alpha channel, so WebKit-only browsers get HEVC with alpha.
export function preferredCartoonLoaderFormat(userAgent: string): CartoonLoaderFormat {
  const isWebKitOnly =
    /AppleWebKit/.test(userAgent) && !/Chrome|Chromium|Edg|Firefox|Android/.test(userAgent)
  return isWebKitOnly ? 'hevc' : 'webm'
}

export function shuffleClipOrder(count: number, random: () => number = Math.random) {
  const order = Array.from({ length: count }, (_, index) => index)
  for (let index = order.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1))
    const current = order[index]
    const swap = order[swapIndex]
    if (current === undefined || swap === undefined) {
      continue
    }
    order[index] = swap
    order[swapIndex] = current
  }
  return order
}

export function nextClipOrder(previous: number[], random: () => number = Math.random) {
  const next = shuffleClipOrder(previous.length, random)
  if (previous.length < 2) {
    return next
  }
  const last = previous.at(-1)
  if (next[0] !== last) {
    return next
  }
  const [head, ...rest] = next
  return head === undefined ? rest : [...rest, head]
}

export interface CartoonLoaderPlaylist {
  order: number[]
  index: number
  upcomingOrder: number[]
}

export function createCartoonLoaderPlaylist(
  count: number,
  random: () => number = Math.random,
): CartoonLoaderPlaylist {
  const order = shuffleClipOrder(count, random)
  return {
    order,
    index: 0,
    upcomingOrder: nextClipOrder(order, random),
  }
}

export function currentCartoonLoaderClip(playlist: CartoonLoaderPlaylist) {
  return playlist.order[playlist.index] ?? 0
}

export function peekNextCartoonLoaderClip(playlist: CartoonLoaderPlaylist) {
  if (playlist.index + 1 < playlist.order.length) {
    return playlist.order[playlist.index + 1] ?? 0
  }
  return playlist.upcomingOrder[0] ?? 0
}

export function advanceCartoonLoaderPlaylist(
  playlist: CartoonLoaderPlaylist,
  random: () => number = Math.random,
): CartoonLoaderPlaylist {
  if (playlist.index + 1 < playlist.order.length) {
    return { ...playlist, index: playlist.index + 1 }
  }
  return {
    order: playlist.upcomingOrder,
    index: 0,
    upcomingOrder: nextClipOrder(playlist.upcomingOrder, random),
  }
}
